import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetAccessPolicyOverrides,
  SaveAccessPolicyOverrides,
  GetSectionAccess,
  type PolicyOverrideMap } from "../Api";
import { ServiceQueryOptions,
  unwrapApiResponse } from "./query_helpers";
import type { SectionPolicy, SectionName } from "../Types";
import { useAuthChecker } from "../Context";
import { getRoleName, Role } from "../Enums";
import { LOOKUP_STALE_TIME } from "../Constant/AppConstants";

const toLower = (s: string): string => s.toLowerCase();

const isAdminRole = (role: string): boolean =>
  toLower(role) === getRoleName(Role.ADMIN);

export const accessPolicyQueryKeys = {
  overrides: () => ["accessPolicyOverrides"] as const,
};

export const useAccessPolicyOverridesQuery = (
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: accessPolicyQueryKeys.overrides(),
    queryFn: async () => unwrapApiResponse(await GetAccessPolicyOverrides()),
  });
};

export const useSaveAccessPolicyOverridesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { overrides: PolicyOverrideMap }) =>
      SaveAccessPolicyOverrides(payload),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: accessPolicyQueryKeys.overrides(),
        });
        queryClient.invalidateQueries({
          queryKey: ["sectionAccess"],
        });
      }
    },
  });
};

type FlatPolicyEntry = {
  path: string;
  defaultRoles: string[];
  mergedRoles: string[];
};

export const useMergedAccessPolicies = () => {
  const { data: overridesData } = useAccessPolicyOverridesQuery();
  const record = overridesData as {
    defaults: PolicyOverrideMap;
    overrides: PolicyOverrideMap;
  } | null;
  const defaults = record?.defaults ?? {};
  const overrideMap = record?.overrides ?? {};

  const flatPolicies = Object.keys(defaults).sort();

  const entries: FlatPolicyEntry[] = flatPolicies.map((path) => {
    const defaultRoles = defaults[path];
    const mergedRoles = overrideMap[path] ?? defaultRoles;
    return { path, defaultRoles, mergedRoles };
  });

  return entries;
};

export const useSyncAccessPolicies = () => {
  const saveMutation = useSaveAccessPolicyOverridesMutation();

  const syncPolicies = async (
    updatedEntries: FlatPolicyEntry[],
  ): Promise<boolean> => {
    const overrides: PolicyOverrideMap = {};

    for (const entry of updatedEntries) {
      const defaults = entry.defaultRoles;
      const changed = entry.mergedRoles;

      const sortedDefault = [...defaults].sort();
      const sortedChanged = [...changed].sort();

      if (JSON.stringify(sortedDefault) !== JSON.stringify(sortedChanged)) {
        overrides[entry.path] = changed;
      }
    }

    const res = await saveMutation.mutateAsync({ overrides });
    return res?.success === true;
  };

  return {
    syncPolicies,
    isSaving: saveMutation.isPending,
  };
};

export const useSectionAccessQuery = (section: string) => {
  return useQuery({
    queryKey: ["sectionAccess", section],
    queryFn: async () => {
      const res = await GetSectionAccess(section);
      return res.data;
    },
    staleTime: LOOKUP_STALE_TIME,
  });
};

export function useSectionAccess<S extends SectionName>(section: S) {
  const { user, isAuthenticated, isAdmin } = useAuthChecker();
  const { data, isLoading } = useSectionAccessQuery(section);

  const role = user?.userRole ?? "none";
  const isSuperAdmin = isAdmin;
  const policies = data?.policies ?? {};

  const isAllowed = (action: SectionPolicy<S>): boolean => {
    if (!isAuthenticated) return false;
    if (isAdminRole(role as string)) return true;

    const entry = (policies as Record<string, { current: string[] }>)[
      action as string
    ];
    const allowedRoles = entry?.current ?? null;

    if (allowedRoles === null) return true;
    return allowedRoles.some((r) => toLower(r) === toLower(role as string));
  };

  const isDenied = (action: SectionPolicy<S>): boolean => !isAllowed(action);

  return {
    isAllowed,
    isDenied,
    isLoading,
    isAuthenticated,
    isSuperAdmin,
  } as const;
}
